import { Component, inject, NgZone, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntil } from "rxjs";
import { first } from "rxjs/operators";

import { LoginComponent as BaseLoginComponent } from "../../../../../../libs/angular/src/components/login.component";
import { ApiService } from "@vault/common/abstractions/api.service";
import { AppIdService } from "@vault/common/abstractions/appId.service";
import { AuthService } from "@vault/common/abstractions/auth.service";
import { CryptoFunctionService } from "@vault/common/abstractions/cryptoFunction.service";
import { EnvironmentService } from "@vault/common/abstractions/environment.service";
import { FormValidationErrorsService } from "@vault/common/abstractions/formValidationErrors.service";
import { I18nService } from "@vault/common/abstractions/i18n.service";
import { LogService } from "@vault/common/abstractions/log.service";
import { LoginService } from "@vault/common/abstractions/login.service";
import { MessagingService } from "@vault/common/abstractions/messaging.service";
import { PasswordGenerationService } from "@vault/common/abstractions/passwordGeneration.service";
import { PlatformUtilsService } from "@vault/common/abstractions/platformUtils.service";
import { PolicyApiServiceAbstraction } from "@vault/common/abstractions/policy/policy-api.service.abstraction";
import { InternalPolicyService } from "@vault/common/abstractions/policy/policy.service.abstraction";
import { UserVerificationService } from "@vault/common/abstractions/userVerification/userVerification.service.abstraction";
import { VerificationType } from "@vault/common/enums/verificationType";
import { PolicyData } from "@vault/common/models/data/policy.data";
import {
  GoogleLogInCredentials,
  PasswordLogInCredentials,
} from "@vault/common/models/domain/log-in-credentials";
import { MasterPasswordPolicyOptions } from "@vault/common/models/domain/master-password-policy-options";
import { Policy } from "@vault/common/models/domain/policy";
import { ListResponse } from "@vault/common/models/response/list.response";
import { PolicyResponse } from "@vault/common/models/response/policy.response";

import { flagEnabled } from "../../../utils/flags";
import { RouterService, StateService } from "../../core";

import { BiometryType } from "@capgo/capacitor-native-biometric";
import { DialogFactory } from "@vault/web-vault/app/components/dialog/dialogFactory";
import {
  BiometricIcon,
  biometryTypeToIcon,
  checkBiometric,
  isCredentialsSaved,
  migrateLegacyFaceIdCredential,
} from "../../capacitor-plugins/native-biometric";
import { ConfirmationDialogService } from "../../services/confirmation/confirmation.service";
import { GoogleIdentityService } from "../../services/api/google-identity.service";
import { FirstDeviceSetupService } from "../../services/DataService/device-trust/first-device-setup.service";
import { ReturningDeviceUnlockService } from "../../services/DataService/device-trust/returning-device-unlock.service";
import { ReferralApi } from "../../services/DataService/referral/referral.api";
import { BiometricSetupHandoffService } from "./biometric-setup-handoff.service";

const isFaceBiometry = (biometryType: BiometryType): boolean =>
  biometryType === BiometryType.FACE_ID || biometryType === BiometryType.FACE_AUTHENTICATION;

@Component({
  selector: "app-login",
  templateUrl: "login.component.html",
})
export class LoginComponent extends BaseLoginComponent implements OnInit, OnDestroy {
  showResetPasswordAutoEnrollWarning = false;
  enforcedPasswordPolicyOptions: MasterPasswordPolicyOptions;
  policies: ListResponse<PolicyResponse>;
  showPasswordless = false;
  showGoogleSignIn = false;
  showBiometrics = false;
  isDevEnv: boolean;
  isLoading = false;
  biometricIcon: BiometricIcon = "fingerprint";

  private pendingBiometricSetup = false;
  // Fingerprint login awaits this so a tap during the first post-upgrade launch doesn't check
  // the generic keychain slot before the legacy Face ID credential has been copied into it.
  private legacyFaceIdMigration: Promise<void> = Promise.resolve();
  private dialogFactory: DialogFactory;
  private confirmationDialogService = inject(ConfirmationDialogService);
  private referralApi = inject(ReferralApi);
  private userVerificationService = inject(UserVerificationService);
  private biometricSetupHandoff = inject(BiometricSetupHandoffService);
  private googleIdentityService = inject(GoogleIdentityService);
  private returningDeviceUnlockService = inject(ReturningDeviceUnlockService);
  private firstDeviceSetupService = inject(FirstDeviceSetupService);

  constructor(
    apiService: ApiService,
    appIdService: AppIdService,
    authService: AuthService,
    router: Router,
    i18nService: I18nService,
    route: ActivatedRoute,
    platformUtilsService: PlatformUtilsService,
    environmentService: EnvironmentService,
    passwordGenerationService: PasswordGenerationService,
    cryptoFunctionService: CryptoFunctionService,
    private policyApiService: PolicyApiServiceAbstraction,
    private policyService: InternalPolicyService,
    logService: LogService,
    ngZone: NgZone,
    protected stateService: StateService,
    private messagingService: MessagingService,
    private routerService: RouterService,
    formBuilder: FormBuilder,
    formValidationErrorService: FormValidationErrorsService,
    loginService: LoginService,
  ) {
    super(
      apiService,
      appIdService,
      authService,
      router,
      platformUtilsService,
      i18nService,
      stateService,
      environmentService,
      passwordGenerationService,
      cryptoFunctionService,
      logService,
      ngZone,
      formBuilder,
      formValidationErrorService,
      route,
      loginService,
    );
    this.dialogFactory = new DialogFactory();
    this.onSuccessfulLogin = async () => {
      this.messagingService.send("setFullWidth");
      await this._applyPendingReferral();
    };
    this.onSuccessfulLoginNavigate = this.goAfterLogIn;
    this.onSuccessfulLoginTwoFactorNavigate = this.goToTwoFactor;
    this.onSuccessfulLoginReturningDeviceUnlock = (encryptedUserKey, privateKey) =>
      this.returningDeviceUnlockService.unlockWithLocalDeviceKey(encryptedUserKey, privateKey);
    this.onSuccessfulLoginFirstDevice = () => this.firstDeviceSetupService.armSetup();
    this.showPasswordless = flagEnabled("showPasswordless");
    this.showGoogleSignIn = flagEnabled("googleSignIn");
    this.isDevEnv = this.platformUtilsService.isMobileDevice();
  }

  get canShowBiometricButton(): boolean {
    return !!this.formGroup.value?.email && this.isDevEnv;
  }

  async ngOnInit() {
    // Back on the login page means any 2FA hop armed for biometric setup was abandoned.
    this.biometricSetupHandoff.clearPendingSetup();
    this.route.queryParams.pipe(first(), takeUntil(this.destroy$)).subscribe((qParams) => {
      (async () => {
        if (qParams.premium != null) {
          this.routerService.setPreviousUrl("/settings/premium");
        } else if (qParams.org != null) {
          const route = this.router.createUrlTree(["create-organization"], {
            queryParams: { plan: qParams.org },
          });
          this.routerService.setPreviousUrl(route.toString());
        }
        if (qParams.sponsorshipToken != null) {
          const route = this.router.createUrlTree(["setup/families-for-enterprise"], {
            queryParams: { token: qParams.sponsorshipToken },
          });
          this.routerService.setPreviousUrl(route.toString());
        }
        if (qParams.referralCode && qParams.email) {
          this.referralApi.storePendingReferralCode(qParams.referralCode, qParams.email);
        }
        await super.ngOnInit();
      })().catch((e) => this.logService.error(e));
    });

    this.legacyFaceIdMigration = this.migrateLegacyFaceIdCredentialIfNeeded();
    await Promise.all([this.legacyFaceIdMigration, this.detectBiometricIcon()]);

    const invite = await this.stateService.getOrganizationInvitation();
    if (invite != null) {
      let policyList: Policy[] = null;
      try {
        this.policies = await this.policyApiService.getPoliciesByToken(
          invite.organizationId,
          invite.token,
          invite.email,
          invite.organizationUserId,
        );
        policyList = this.policyService.mapPoliciesFromToken(this.policies);
      } catch (e) {
        this.logService.error(e);
      }

      if (policyList != null) {
        const resetPasswordPolicy = this.policyService.getResetPasswordPolicyOptions(
          policyList,
          invite.organizationId,
        );
        this.showResetPasswordAutoEnrollWarning =
          resetPasswordPolicy[1] && resetPasswordPolicy[0].autoEnrollEnabled;

        this.policyService
          .masterPasswordPolicyOptions$(policyList)
          .pipe(takeUntil(this.destroy$))
          .subscribe((enforcedPasswordPolicyOptions) => {
            this.enforcedPasswordPolicyOptions = enforcedPasswordPolicyOptions;
          });
      }
    }
    await this.stateService.setBiometricFingerprintValidated(false);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  async goAfterLogIn() {
    if (this.pendingBiometricSetup) {
      this.pendingBiometricSetup = false;
      // Capture the hash before navigating — the two-factor-setup screen may
      // read it as soon as its route resolves, so it must already be stashed.
      try {
        await this.captureBiometricSetupHash(this.formGroup.value.password);
      } catch (e) {
        this.logService.error(e);
        // A failed capture must not leave a stale hash from an earlier, unrelated
        // attempt reachable — the setup screen would otherwise consume it instead of
        // falling back to the password prompt, building a credential for the wrong password.
        this.biometricSetupHandoff.clear();
      }
      this.router.navigate(["/two-factor-setup"], { queryParams: { setupBiometrics: "true" } });
      return;
    }

    const masterPassword = this.formGroup.value.password;

    // Check master password against policy. Google and saved-credential biometric logins
    // leave the password field empty, so there is nothing to evaluate.
    if (this.enforcedPasswordPolicyOptions != null && masterPassword) {
      const strengthResult = this.passwordGenerationService.passwordStrength(
        masterPassword,
        this.getPasswordStrengthUserInput(),
      );
      const masterPasswordScore = strengthResult == null ? null : strengthResult.score;

      // If invalid, save policies and require update
      if (
        !this.policyService.evaluateMasterPassword(
          masterPasswordScore,
          masterPassword,
          this.enforcedPasswordPolicyOptions,
        )
      ) {
        const policiesData: { [id: string]: PolicyData } = {};
        this.policies.data.map((p) => (policiesData[p.id] = new PolicyData(p)));
        await this.policyService.replace(policiesData);
        this.router.navigate(["update-password"]);
        return;
      }
    }

    const previousUrl = this.routerService.getPreviousUrl();

    if (previousUrl) {
      this.router.navigateByUrl(previousUrl);
    } else {
      this.loginService.clearValues();
      this.router.navigate([this.successRoute]);
    }
  }

  // Navigating to 2fa destroys this component and pendingBiometricSetup with it, so hand
  // the intent to the root-scoped handoff service for TwoFactorComponent to pick up.
  async goToTwoFactor() {
    if (this.pendingBiometricSetup) {
      this.pendingBiometricSetup = false;
      // No hash can be captured before 2FA completes; drop any earlier one so the setup
      // screen can't consume a hash from an unrelated attempt.
      this.biometricSetupHandoff.clear();
      this.biometricSetupHandoff.armPendingSetup();
    } else {
      this.biometricSetupHandoff.clearPendingSetup();
    }
    this.router.navigate([this.twoFactorRoute]);
  }

  handleCreateAccount() {
    // todo for Iphone pwa registrtation needs to be handled differently
    // if( this.deviceDetector.isIphoneDevice() )  {}
    // else window.location = this.stripeConfigService.gotoSubscriptionPage();

    // to deploy the app on iphone, we just keep the whole registration process in MVP
    this.router.navigate(["/register"]);
  }

  goToHint() {
    this.setFormValues();
    this.router.navigateByUrl("/hint");
  }

  override async submit() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    // Ordinary password login never wants the biometric-setup route; clear any
    // flag stranded by a prior failed loginWithFingerprint() attempt.
    this.pendingBiometricSetup = false;
    // Everything after isLoading is set must sit inside the try, or a throw
    // here would leave isLoading stuck true and the login page inert.
    try {
      // Yield to the event loop so the browser can repaint before password
      // hashing (PBKDF2) blocks the main thread.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));

      const rememberEmail = this.formGroup.value.rememberEmail;
      if (!rememberEmail) {
        await this.stateService.setRememberedEmail(null);
      }
      await super.submit(false);
      this.formGroup.get("rememberEmail")?.setValue(true);
    } finally {
      this.isLoading = false;
    }
  }

  async startPasswordlessLogin() {
    this.formGroup.get("masterPassword")?.clearValidators();
    this.formGroup.get("masterPassword")?.updateValueAndValidity();

    if (!this.formGroup.valid) {
      return;
    }

    this.setFormValues();
    this.router.navigate(["/login-with-device"]);
  }

  async loginWithFingerprint() {
    // isLoading is checked and set synchronously, before any await, so a rapid
    // second click/tap sees it already true and returns here — before it can
    // touch pendingBiometricSetup and clobber an in-flight attempt's flag.
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    // Fresh attempt starts clean — do not rely on the catch below to clear a
    // stale flag left by a prior failed attempt (super.submit(false) resolves
    // without throwing on ordinary auth failures, so that catch never fires then).
    this.pendingBiometricSetup = false;

    try {
      if (!this.formGroup.get("email").valid) {
        return;
      }

      const biometricStatus = await checkBiometric();
      if (!biometricStatus.isAvailable) {
        return this.confirmationDialogService.notify(
          "error",
          "auth",
          this.i18nService.t("biometricNotSupported"),
          false,
        );
      }

      await this.legacyFaceIdMigration;
      const isSetupDone = await isCredentialsSaved();
      if (isSetupDone) {
        await super.loginWithCredential(new PasswordLogInCredentials("", "", undefined, undefined));
        return;
      }

      const password = this.formGroup.value?.password;
      if (!password || password.trim() === "") {
        return this.confirmationDialogService.notify(
          "error",
          "auth",
          this.i18nService.t(
            isFaceBiometry(biometricStatus.biometryType)
              ? "faceIdSetupRequired"
              : "fingerprintSetupRequired",
          ),
          false,
        );
      }

      // Credentials not saved yet — log in with password then redirect to biometric setup.
      // goAfterLogIn (wired as onSuccessfulLoginNavigate) captures the hash once
      // login fully succeeds — don't duplicate that call here.
      this.pendingBiometricSetup = true;
      try {
        await super.submit(false);
      } catch (e) {
        this.pendingBiometricSetup = false;
        this.logService.error(String(e));
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Identity-only path — no vault key material comes from Google. On success, a
   * brand-new account with no trusted device yet (`isFirstDevice`) runs the TDE
   * first-device setup flow (`first-device-setup` route) instead of ever prompting
   * for a master password; see `FirstDeviceSetupComponent`/`FirstDeviceSetupService`
   * and GOOGLE_SSO_PLAN.md. The password-based `resetMasterPassword`/`set-password`
   * branch (see base `LoginComponent`) is the fallback for any other keyless account.
   */
  async loginWithGoogle() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.pendingBiometricSetup = false;

    try {
      const idToken = await this.googleIdentityService.getIdToken();
      await super.loginWithCredential(new GoogleLogInCredentials(idToken));
    } catch (e) {
      this.logService.error(e);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * super.submit(false) resolves without throwing even when login only partially
   * completed (2FA/captcha challenge issued) — the active crypto key is only set
   * on a fully successful login (see LogInStrategy.processTokenResponse vs.
   * processTwoFactorResponse/processCaptchaResponse). Guard on getIsAuthenticated()
   * so we never hash against a stale or absent key for an account that isn't
   * actually logged in yet. Also guard on password being present since
   * goAfterLogIn passes formGroup.value.password, which may be null/undefined.
   */
  private async captureBiometricSetupHash(password: string | null | undefined): Promise<void> {
    if (!password || !(await this.stateService.getIsAuthenticated())) {
      return;
    }
    // Goes through the exact same call TwoFactorVerifyComponent.submit() makes from a
    // re-entered password, so the two are guaranteed identical by shared code rather than
    // by two independent call sites that merely happen to compute the same thing today.
    const request = await this.userVerificationService.buildRequest({
      type: VerificationType.MasterPassword,
      secret: password,
    });
    this.biometricSetupHandoff.setHash(request.masterPasswordHash);
  }

  async onContinue() {
    return await this.validateEmail();
  }

  private getPasswordStrengthUserInput() {
    const email = this.formGroup.value.email;
    let userInput: string[] = [];
    const atPosition = email.indexOf("@");
    if (atPosition > -1) {
      userInput = userInput.concat(
        email
          .substr(0, atPosition)
          .trim()
          .toLowerCase()
          .split(/[^A-Za-z0-9]/),
      );
    }
    return userInput;
  }

  private async _applyPendingReferral(): Promise<void> {
    await this.referralApi
      .applyPending(this.formGroup.value.email)
      .catch((e) => this.logService.error(`Referral apply failed: ${e}`));
  }

  goSupport() {
    this.dialogFactory.callHelpBox();
  }

  private async migrateLegacyFaceIdCredentialIfNeeded(): Promise<void> {
    if (!this.isDevEnv) {
      return;
    }
    try {
      if (await this.stateService.getLegacyFaceIdMigrated()) {
        return;
      }
      await migrateLegacyFaceIdCredential();
      await this.stateService.setLegacyFaceIdMigrated(true);
    } catch (e) {
      this.logService.error(String(e));
    }
  }

  private async detectBiometricIcon(): Promise<void> {
    if (!this.isDevEnv) {
      return;
    }
    try {
      const { biometryType } = await checkBiometric();
      this.biometricIcon = biometryTypeToIcon(biometryType);
    } catch (e) {
      this.logService.error(String(e));
    }
  }
}
